import { useOnboarding, PRICING } from "./OnboardingContext";
import { Minus, Plus, Lock, Upload, Check, ArrowLeft, ArrowRight, Info, X, Droplets, Tag, Loader2 } from "lucide-react";
import { useRef, useState } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StepConfiguration = () => {
  const {
    selectedPackage,
    numberOfMachines,
    selectedAddOns,
    logoFile,
    promoCode,
    promoApplied,
    promoDiscount,
    promoMonths,
    setMachines,
    toggleAddOn,
    setLogoFile,
    applyPromoCode,
    removePromoCode,
    nextStep,
    prevStep,
    getPackageDetails
  } = useOnboarding();

  const [dragActive, setDragActive] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [promoSuccess, setPromoSuccess] = useState("");
  const fileInputRef = useRef(null);
  const pkg = getPackageDetails();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file.type.startsWith("image/")) {
      setLogoFile({
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file)
      });
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePromoValidation = async () => {
    if (!promoInput.trim()) {
      setPromoError("Veuillez entrer un code promo");
      return;
    }

    setPromoLoading(true);
    setPromoError("");
    setPromoSuccess("");

    try {
      const response = await fetch(`${API_URL}/api/promo/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: promoInput.trim() })
      });

      const data = await response.json();

      if (data.valid) {
        applyPromoCode(data.code, data.discount_percent, data.discount_months);
        setPromoSuccess(data.message);
        setPromoError("");
      } else {
        setPromoError(data.message);
        setPromoSuccess("");
      }
    } catch (error) {
      setPromoError("Erreur de validation. Réessayez.");
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    removePromoCode();
    setPromoInput("");
    setPromoError("");
    setPromoSuccess("");
  };

  if (!pkg) return null;

  const additionalMachines = Math.max(0, numberOfMachines - 1);
  const machinesCost = additionalMachines * pkg.basePrice;

  const addOns = Object.values(PRICING.addOns);

  return (
    <div data-testid="step-configuration" className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <p className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-2">
          Étape 3
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-[#0A0A0A] mb-4">
          Configuration machine
        </h1>
        <p className="font-body text-[#525252]">
          Personnalisez votre installation selon vos besoins.
        </p>
      </div>

      {/* Package Constraints Banner */}
      <div className="bg-[#0A0A0A] text-white p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Droplets size={24} className="text-[#B89F7C]" />
          <h3 className="font-heading text-xl">Offre {pkg.name}</h3>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <p className="font-body text-xs uppercase tracking-wider text-white/60 mb-1">Flacons inclus</p>
            <p className="font-heading text-2xl text-[#B89F7C]">{pkg.flacons}</p>
          </div>
          <div>
            <p className="font-body text-xs uppercase tracking-wider text-white/60 mb-1">Affichage digital</p>
            <p className="font-heading text-2xl">{pkg.digitalDisplay}%</p>
          </div>
          <div>
            <p className="font-body text-xs uppercase tracking-wider text-white/60 mb-1">Engagement</p>
            <p className="font-heading text-2xl">{pkg.engagement} mois</p>
          </div>
        </div>
      </div>

      {/* Machines Section */}
      <div className="bg-white border border-[#E5E5E5] p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="font-heading text-xl text-[#0A0A0A] mb-1">
              Nombre de machines
            </h3>
            <p className="font-body text-sm text-[#525252]">
              Première machine incluse. +{pkg.basePrice}€ HT/mois par machine additionnelle.
            </p>
            {machinesCost > 0 && (
              <p className="font-body text-sm text-[#B89F7C] mt-1">
                +{machinesCost}€ HT/mois
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMachines(numberOfMachines - 1)}
              disabled={numberOfMachines <= 1}
              data-testid="btn-machines-minus"
              className="w-10 h-10 border border-[#E5E5E5] flex items-center justify-center hover:border-[#0A0A0A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Minus size={16} />
            </button>
            <span className="font-heading text-2xl text-[#0A0A0A] w-12 text-center">
              {numberOfMachines}
            </span>
            <button
              onClick={() => setMachines(numberOfMachines + 1)}
              data-testid="btn-machines-plus"
              className="w-10 h-10 border border-[#E5E5E5] flex items-center justify-center hover:border-[#0A0A0A] transition-colors"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Flacons Info */}
      <div className="bg-[#F9F6F0] border border-[#E5E5E5] p-6 mb-6">
        <div className="flex items-start gap-4">
          <Droplets size={24} className="text-[#B89F7C] shrink-0" />
          <div>
            <h3 className="font-heading text-lg text-[#0A0A0A] mb-1">
              {pkg.flacons} flacons inclus
            </h3>
            <p className="font-body text-sm text-[#525252]">
              {pkg.mlPerFlacon}ML par flacon par trimestre. Modification des fragrances possible après {pkg.fragranceModification} jours.
            </p>
          </div>
        </div>
      </div>

      {/* Add-ons Section - Pre-order optimized */}
      <div className="mb-6">
        <h3 className="font-body text-sm tracking-[0.15em] uppercase text-[#525252] mb-4">
          Options de préparation
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {addOns.map((addon) => {
            const isSelected = selectedAddOns.includes(addon.id);
            return (
              <button
                key={addon.id}
                onClick={() => toggleAddOn(addon.id)}
                data-testid={`addon-${addon.id}`}
                className={`p-5 text-left border transition-all duration-300 ${
                  isSelected
                    ? "bg-[#0A0A0A] border-[#0A0A0A] text-white"
                    : "bg-white border-[#E5E5E5] hover:border-[#B89F7C]"
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <h4 className={`font-body font-medium text-sm ${isSelected ? "text-white" : "text-[#0A0A0A]"}`}>
                    {addon.name}
                  </h4>
                  {isSelected && (
                    <Check size={16} className="text-[#B89F7C]" />
                  )}
                </div>
                {addon.subtitle && (
                  <p className={`font-body text-xs mb-2 ${isSelected ? "text-[#B89F7C]" : "text-[#B89F7C]"}`}>
                    {addon.subtitle}
                  </p>
                )}
                <p className={`font-body text-xs mb-3 ${isSelected ? "text-white/70" : "text-[#525252]"}`}>
                  {addon.description}
                </p>
                {addon.microcopy && (
                  <p className={`font-body text-[10px] mb-2 italic ${isSelected ? "text-white/50" : "text-[#525252]/70"}`}>
                    {addon.microcopy}
                  </p>
                )}
                <p className={`font-body text-sm font-medium ${isSelected ? "text-[#B89F7C]" : "text-[#B89F7C]"}`}>
                  +{addon.price}€ HT/mois
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Logo Customization */}
      {pkg.logoCustomization ? (
        <div className="bg-white border border-[#E5E5E5] p-6 mb-6">
          <h3 className="font-heading text-xl text-[#0A0A0A] mb-4">
            Personnalisation du logo
          </h3>
          <p className="font-body text-sm text-[#525252] mb-4">
            Uploadez votre logo pour l'afficher sur les diffuseurs.
          </p>

          {logoFile ? (
            <div className="border border-[#E5E5E5] p-4 flex items-center gap-4">
              <img
                src={logoFile.url}
                alt="Logo preview"
                className="w-16 h-16 object-contain bg-[#F9F6F0]"
              />
              <div className="flex-1">
                <p className="font-body text-sm font-medium text-[#0A0A0A]">{logoFile.name}</p>
                <p className="font-body text-xs text-[#525252]">
                  {(logoFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                onClick={removeLogo}
                className="w-8 h-8 flex items-center justify-center text-[#525252] hover:text-[#0A0A0A]"
              >
                <X size={18} />
              </button>
            </div>
          ) : (
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed p-8 text-center transition-colors ${
                dragActive ? "border-[#B89F7C] bg-[#F9F6F0]" : "border-[#E5E5E5]"
              }`}
            >
              <Upload size={32} className="mx-auto mb-4 text-[#B89F7C]" />
              <p className="font-body text-sm text-[#525252] mb-2">
                Glissez-déposez votre logo ici
              </p>
              <p className="font-body text-xs text-[#525252] mb-4">ou</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInput}
                className="hidden"
                data-testid="logo-file-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                data-testid="btn-upload-logo"
                className="bg-transparent border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#FAFAFA] transition-colors px-6 py-2 text-sm tracking-widest uppercase font-medium"
              >
                Parcourir
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-6 mb-6">
          <div className="flex items-start gap-4">
            <Lock size={24} className="text-[#525252] shrink-0" />
            <div>
              <h3 className="font-heading text-lg text-[#0A0A0A] mb-1">
                Personnalisation du logo
              </h3>
              <p className="font-body text-sm text-[#525252]">
                La personnalisation du logo est disponible uniquement avec l'offre VIP.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Promo Code Section */}
      <div className="bg-white border border-[#E5E5E5] p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Tag size={20} className="text-[#B89F7C]" />
          <h3 className="font-heading text-lg text-[#0A0A0A]">
            Code promo
          </h3>
        </div>

        {promoApplied ? (
          <div className="bg-[#F0FDF4] border border-[#86EFAC] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-sm font-medium text-[#166534]">
                  ✓ Code {promoCode} appliqué
                </p>
                <p className="font-body text-xs text-[#166534]/80 mt-1">
                  -{promoDiscount}% sur l'abonnement pendant {promoMonths} mois
                </p>
              </div>
              <button
                onClick={handleRemovePromo}
                className="text-[#166534] hover:text-[#14532D] text-sm underline"
              >
                Retirer
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex gap-3">
              <input
                type="text"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                placeholder="Entrez votre code"
                data-testid="promo-code-input"
                className="flex-1 border border-[#E5E5E5] px-4 py-3 font-body text-sm focus:outline-none focus:border-[#B89F7C] transition-colors uppercase"
              />
              <button
                onClick={handlePromoValidation}
                disabled={promoLoading}
                data-testid="btn-apply-promo"
                className="bg-[#0A0A0A] text-white px-6 py-3 font-body text-sm tracking-wider uppercase hover:bg-[#B89F7C] transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {promoLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  "Appliquer"
                )}
              </button>
            </div>
            {promoError && (
              <p className="font-body text-xs text-red-600 mt-2">{promoError}</p>
            )}
            {promoSuccess && (
              <p className="font-body text-xs text-[#166534] mt-2">{promoSuccess}</p>
            )}
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="bg-white border border-[#E5E5E5] p-4 mb-8 flex items-start gap-3">
        <Info size={18} className="text-[#B89F7C] mt-0.5 shrink-0" />
        <p className="font-body text-sm text-[#525252]">
          Le nombre de flacons est fixé selon votre offre ({pkg.flacons} flacons pour l'offre {pkg.name}). 
          Vous pouvez augmenter le nombre de machines selon vos besoins.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-[#E5E5E5]">
        <button
          onClick={prevStep}
          data-testid="btn-prev-step"
          className="flex items-center gap-2 font-body text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors"
        >
          <ArrowLeft size={16} />
          Retour
        </button>
        <button
          onClick={nextStep}
          data-testid="btn-next-step"
          className="bg-[#0A0A0A] text-white hover:bg-[#B89F7C] transition-colors duration-300 px-8 py-3 text-sm tracking-widest uppercase font-medium flex items-center gap-2"
        >
          Continuer
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default StepConfiguration;
